/**
 * ListManagerPlugin.ts - OpCentral List Library Manager Plugin
 * 
 * CHANGE HISTORY:
 * - 251130_1640 danielbeer: Created multi-package plugin in Pluggr
 * - 2025-11-29 danielbeer: Original version in fmwvtool-v4
 * 
 * ARCHITECTURE:
 * - Multi-package plugin (Outlinr + Organizr + Listr)
 * - Location: packages/pluggr/src/plugins/list-manager/
 * - Uses Organizr APIs exclusively (no direct fetch)
 * - Imports ListManagerView from Organizr package
 * - Registers FileMaker functions for list operations
 * 
 * FEATURES:
 * - Manage OpCentral list library
 * - Outlinr-based hierarchical editing
 * - Import/export JSON lists
 * - Git-friendly one-file-per-list format
 * - FileMaker integration
 * 
 * DEPENDENCIES:
 * - @opcentral/organizr (list management + UI)
 * - @opcentral/outlinr (hierarchical editing)
 * - @opcentral/listr (dynamic lists)
 */

import type { Plugin, PluginContext } from '@opcentral/dependr';
import { getOrganizr, ListManagerView } from '@opcentral/organizr';

/**
 * List Manager Plugin
 * 
 * Provides a complete list management interface with FileMaker integration.
 */
const ListManagerPlugin: Plugin = {
  manifest: {
    id: 'list-manager',
    name: 'List Manager',
    version: '2.0.0',
    description: 'Manage OpCentral list library with Outlinr and Organizr',
    author: 'OpCentral',
    main: 'ListManagerPlugin.ts',
    category: 'data',
    tags: ['lists', 'organizr', 'outlinr', 'listr', 'data-management'],
    permissions: {
      files: 'read-write',
      network: 'restricted', // For API calls
      system: 'none'
    },
    features: [
      'list-management',
      'organizr-integration',
      'outlinr-editor',
      'listr-support',
      'json-import-export',
      'git-version-control',
      'filemaker-integration'
    ],
    dependencies: {
      '@opcentral/organizr': '*',
      '@opcentral/outlinr': '*',
      '@opcentral/listr': '*'
    }
  },

  async onload(context: PluginContext) {
    console.log('[ListManagerPlugin] Loading...');
    
    // Get Organizr instance
    const organizr = getOrganizr();
    
    // Register UI component
    if (context.ui?.registerComponent) {
      context.ui.registerComponent('list-manager', ListManagerView);
      console.log('[ListManagerPlugin] Registered ListManagerView component');
    }
    
    // Register FileMaker functions for list operations
    
    /**
     * fm_listManager_getList - Load a list from the library
     * 
     * @param params.listId - List ID (e.g., "common-priorities")
     * @returns {success, data} - List data in Organizr format
     * 
     * FileMaker Example:
     * Set Variable [ $json ; JSONSetElement ( "{}" ; "listId" ; "common-priorities" ; JSONString ) ]
     * Perform JavaScript in Web Viewer [ Function: "fm_listManager_getList" ; Parameters: $json ]
     */
    context.registerFunction?.('fm_listManager_getList', async (params: any) => {
      try {
        const { listId } = params;
        
        if (!listId) {
          throw new Error('listId parameter is required');
        }
        
        // Use Organizr API to load list
        const listData = await organizr.loadListFromAPI(listId);
        
        return { 
          success: true, 
          data: listData 
        };
      } catch (error) {
        console.error('[ListManagerPlugin] fm_listManager_getList error:', error);
        return { 
          success: false, 
          error: error instanceof Error ? error.message : String(error) 
        };
      }
    });

    /**
     * fm_listManager_saveList - Save a list to the library
     * 
     * @param params.listId - List ID (e.g., "common-priorities")
     * @param params.data - List data in Organizr format
     * @returns {success, message} - Save result
     * 
     * FileMaker Example:
     * Set Variable [ $json ; JSONSetElement ( "{}" ; 
     *   ["listId" ; "common-priorities" ; JSONString ] ;
     *   ["data" ; $listData ; JSONObject ]
     * )]
     * Perform JavaScript in Web Viewer [ Function: "fm_listManager_saveList" ; Parameters: $json ]
     */
    context.registerFunction?.('fm_listManager_saveList', async (params: any) => {
      try {
        const { listId, data } = params;
        
        if (!listId) {
          throw new Error('listId parameter is required');
        }
        if (!data) {
          throw new Error('data parameter is required');
        }
        
        // Use Organizr API to save list
        const result = await organizr.saveListToAPI(listId, data);
        
        if (result.success) {
          return { 
            success: true, 
            message: result.message || 'List saved successfully' 
          };
        } else {
          throw new Error(result.error || 'Save failed');
        }
      } catch (error) {
        console.error('[ListManagerPlugin] fm_listManager_saveList error:', error);
        return { 
          success: false, 
          error: error instanceof Error ? error.message : String(error) 
        };
      }
    });

    /**
     * fm_listManager_getAllLists - Get all lists from registry
     * 
     * @param params.includeItemCounts - Whether to include item counts (optional, default: false)
     * @returns {success, data} - Array of all lists in library
     * 
     * FileMaker Example:
     * Set Variable [ $json ; JSONSetElement ( "{}" ; "includeItemCounts" ; True ; JSONBoolean ) ]
     * Perform JavaScript in Web Viewer [ Function: "fm_listManager_getAllLists" ; Parameters: $json ]
     */
    context.registerFunction?.('fm_listManager_getAllLists', async (params: any = {}) => {
      try {
        const { includeItemCounts = false } = params;
        
        // Use Organizr API to load registry
        const lists = await organizr.loadListRegistry({
          includeItemCounts,
          apiEndpoint: '/api/lists/registry'
        });
        
        return { 
          success: true, 
          data: lists 
        };
      } catch (error) {
        console.error('[ListManagerPlugin] fm_listManager_getAllLists error:', error);
        return { 
          success: false, 
          error: error instanceof Error ? error.message : String(error) 
        };
      }
    });

    /**
     * fm_listManager_exportList - Export a list as JSON
     * 
     * @param params.listId - List ID to export
     * @returns {success, data} - JSON string of list data
     * 
     * FileMaker Example:
     * Set Variable [ $json ; JSONSetElement ( "{}" ; "listId" ; "common-priorities" ; JSONString ) ]
     * Perform JavaScript in Web Viewer [ Function: "fm_listManager_exportList" ; Parameters: $json ]
     */
    context.registerFunction?.('fm_listManager_exportList', async (params: any) => {
      try {
        const { listId } = params;
        
        if (!listId) {
          throw new Error('listId parameter is required');
        }
        
        // Load list and export as JSON
        const listData = await organizr.loadListFromAPI(listId);
        const json = JSON.stringify(listData, null, 2);
        
        return { 
          success: true, 
          data: json 
        };
      } catch (error) {
        console.error('[ListManagerPlugin] fm_listManager_exportList error:', error);
        return { 
          success: false, 
          error: error instanceof Error ? error.message : String(error) 
        };
      }
    });

    /**
     * fm_listManager_importList - Import a list from JSON
     * 
     * @param params.listId - List ID to import to
     * @param params.json - JSON string of list data
     * @returns {success, message} - Import result
     * 
     * FileMaker Example:
     * Set Variable [ $json ; JSONSetElement ( "{}" ; 
     *   ["listId" ; "common-priorities" ; JSONString ] ;
     *   ["json" ; $jsonData ; JSONString ]
     * )]
     * Perform JavaScript in Web Viewer [ Function: "fm_listManager_importList" ; Parameters: $json ]
     */
    context.registerFunction?.('fm_listManager_importList', async (params: any) => {
      try {
        const { listId, json } = params;
        
        if (!listId) {
          throw new Error('listId parameter is required');
        }
        if (!json) {
          throw new Error('json parameter is required');
        }
        
        // Parse JSON and save
        const listData = JSON.parse(json);
        const result = await organizr.saveListToAPI(listId, listData);
        
        if (result.success) {
          return { 
            success: true, 
            message: 'List imported successfully' 
          };
        } else {
          throw new Error(result.error || 'Import failed');
        }
      } catch (error) {
        console.error('[ListManagerPlugin] fm_listManager_importList error:', error);
        return { 
          success: false, 
          error: error instanceof Error ? error.message : String(error) 
        };
      }
    });

    console.log('[ListManagerPlugin] Loaded successfully');
    console.log('[ListManagerPlugin] Registered 5 FileMaker functions');
  },

  async onunload() {
    console.log('[ListManagerPlugin] Unloading...');
    // Cleanup if needed
  }
};

export default ListManagerPlugin;
